/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';
import { StoreState } from 'store/store';

import {
  ActionSuccess,
  Ducks,
  Seconds,
  SsmApi,
  SecuritySessionStart,
  SecuritySessionRenewal,
  XError,
  SecuritySessionBookSlot,
  ActionRequest,
  SsmErrors,
} from '@wit/vtv-x-foundation-lib';

export enum ActionType {
  UPDATE = 'ssm/data/UPDATE',
  CLEAR = 'ssm/data/CLEAR',
}

export type SsmLicenseParams = {
  drmId: string;
  challenge: string;
};

// ACTIONS

type Update = ActionSuccess<ActionType.UPDATE, Partial<SsmReducerState>>;
type Clear = ActionRequest<ActionType.CLEAR>;
type Actions = Update | Clear;

const Actions = {
  update: (payload: Partial<SsmReducerState>): Update => ({ type: ActionType.UPDATE, payload }),
  clear: (): Clear => ({ type: ActionType.CLEAR }),
};

export const ActionCreators = {
  update: Actions.update,
};

// REDUCER
export type LicenseData = {
  license: string;
  heartbeat: Seconds;
};

export interface SsmReducerState {
  heartbeat?: Seconds;
  sessionToken?: string;
  license?: LicenseData;
  error?: XError;
}

const initialState: SsmReducerState = {};

export default function ssm(state = initialState, action: Actions): SsmReducerState {
  switch (action.type) {
    case ActionType.UPDATE:
      return {
        ...state,
        ...action.payload,
      };

    case ActionType.CLEAR:
      return initialState;
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  isValidSession: (state: StoreState): boolean => state.ssm !== initialState,
  data: (state: StoreState): SsmReducerState => state.ssm,
  heartbeat: (state: StoreState): Seconds | undefined => state.ssm.heartbeat,
  license: (state: StoreState): LicenseData | undefined => state.ssm.license,
  error: (state: StoreState): XError | undefined => state.ssm.error,
};

// Storage sync utils
// only used in web - so we need to move to a separate service/duck when this duck be moved to the lib

function saveStopSessionData(sessionParams: Record<string, unknown>, bookmarkBody: Record<string, unknown>) {
  localStorage.setItem('ssm-session', JSON.stringify({ sessionParams, bookmarkBody }));
}
function removeStopSessionData() {
  localStorage.removeItem('ssm-session');
}
function getStopSessionData():
  | { sessionParams: Record<string, unknown>; bookmarkBody: Record<string, unknown> }
  | undefined {
  const prevSession = localStorage.getItem('ssm-session');

  if (prevSession) {
    removeStopSessionData();
    return JSON.parse(prevSession);
  }

  return undefined;
}

// SAGAS

function* getLicense(
  entitlementRequestBody: Record<string, unknown>,
  bookmarkBody: Record<string, unknown>,
  licenseParams: SsmLicenseParams
): SagaIterator<SecuritySessionStart | undefined> {
  try {
    const sessionRes: SecuritySessionStart = yield Ducks.Api.callApi(
      SsmApi.start,
      entitlementRequestBody,
      bookmarkBody,
      licenseParams
    );

    yield put(
      Actions.update({
        heartbeat: sessionRes.heartbeat,
        sessionToken: sessionRes.sessionToken,
        license: { license: sessionRes.license, heartbeat: sessionRes.heartbeat },
      })
    );

    return sessionRes;
  } catch (error) {
    yield put(Actions.update({ error: XError.fromError(error, SsmErrors.generic.start) }));
    return undefined;
  }
}

function* renewLicense(
  bookmarkBody: Record<string, unknown>,
  licenseParams?: SsmLicenseParams
): SagaIterator<SecuritySessionRenewal | undefined> {
  try {
    const session: SsmReducerState = yield select(Selectors.data);

    const sessionRes: SecuritySessionRenewal = yield Ducks.Api.callApi(
      SsmApi.renewal,
      { sessionToken: session.sessionToken },
      bookmarkBody,
      licenseParams || {}
    );

    const heartbeat: Seconds = yield select(Selectors.heartbeat);

    yield put(
      Actions.update({
        sessionToken: sessionRes.sessionToken,
        license: { license: sessionRes.license, heartbeat },
      })
    );

    return sessionRes;
  } catch (error) {
    yield put(Actions.update({ error: XError.fromError(error, SsmErrors.generic.renewal) }));
    return undefined;
  }
}

function* stopPreviousStoredSession(curKs: string): SagaIterator {
  try {
    const prevStopSessionData = getStopSessionData();
    if (prevStopSessionData) {
      prevStopSessionData.bookmarkBody.ks = curKs;
      yield Ducks.Api.callApi(SsmApi.tearDown, prevStopSessionData.sessionParams, prevStopSessionData.bookmarkBody);
    }
  } catch {
    // do nothing
  }
}

function* startSession(bookmarkBody: Record<string, unknown>): SagaIterator<SecuritySessionBookSlot> {
  try {
    yield call(stopPreviousStoredSession, bookmarkBody.ks as string);

    const sessionRes: SecuritySessionBookSlot = yield Ducks.Api.callApi(SsmApi.bookSlot, bookmarkBody);

    yield put(Actions.update(sessionRes));

    return sessionRes;
  } catch (error) {
    throw XError.fromError(error, SsmErrors.generic.bookSlot);
  }
}

function* stopSession(bookmarkBody: Record<string, unknown>) {
  try {
    const session: SsmReducerState = yield select(Selectors.data);

    if (session !== initialState) {
      const sessionParams = { sessionToken: session.sessionToken };
      saveStopSessionData(sessionParams, bookmarkBody); // save data to be sent later if the browser closes the app without complete the request
      yield Ducks.Api.callApi(SsmApi.tearDown, sessionParams, bookmarkBody);
      removeStopSessionData(); // since the request was completed remove data from storage
    }
  } catch {
    // do nothing
  } finally {
    yield put(Actions.clear());
  }
}

function* keepSession(bookmarkBody: Record<string, unknown>) {
  yield call(renewLicense, bookmarkBody);
}

export const SagaFunctions = {
  startSession,
  keepSession,
  stopSession,
  getLicense,
  renewLicense,
};
